import * as React from 'react';
import cn from 'classnames';
import styles from './AdminSettingsSection.module.scss';

type AdminSettingsSectionProps = {
  className?: string;
  title: string;
  // When true, the title becomes a toggle that shows/hides the body.
  collapsible?: boolean;
  // Initial collapsed state (only meaningful when `collapsible`).
  defaultCollapsed?: boolean;
  // Optional CONTROLLED mode: pass `collapsed` (and normally `onCollapsedChange`)
  // to drive the fold from the parent, so something outside the section can open
  // it — e.g. a "Choose a plan" button elsewhere on the page. Omit both and the
  // section keeps its own state exactly as before.
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
};

export const AdminSettingsSection: React.FC<AdminSettingsSectionProps> = ({
  children,
  className,
  title,
  collapsible = false,
  defaultCollapsed = false,
  collapsed: collapsedProp,
  onCollapsedChange,
}) => {
  const [collapsedState, setCollapsedState] = React.useState(
    collapsible && defaultCollapsed,
  );
  const isControlled = collapsedProp !== undefined;
  const collapsed = isControlled ? collapsedProp : collapsedState;
  const toggle = () => {
    const next = !collapsed;
    if (!isControlled) setCollapsedState(next);
    onCollapsedChange?.(next);
  };

  if (!collapsible) {
    return (
      <div className={cn([styles.AdminSettingsSection, className])}>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.card}>{children}</div>
      </div>
    );
  }

  return (
    <div className={cn([styles.AdminSettingsSection, className])}>
      <button
        type="button"
        className={cn(styles.title, styles.titleToggle)}
        aria-expanded={!collapsed}
        onClick={toggle}
      >
        <span
          className={cn(styles.chevron, { [styles.chevronOpen]: !collapsed })}
          aria-hidden="true"
        />
        {title}
      </button>
      {!collapsed && <div className={styles.card}>{children}</div>}
    </div>
  );
};

export default AdminSettingsSection;
